import { useContext, useEffect, useState } from 'react';

import { useInitialize } from '@kameleoon/react-sdk';
import mixpanel from 'mixpanel-browser';

import { ApplicationContext } from '@App/contexts/Application';
import { ExperimentsConfig } from '@App/utils/experiments';
import { KAMELEOON_FEATURE, sendFeatureToMixPanel } from '@App/utils/kameleoon';

import { useKameleoonFeature } from './useKameleoon';

interface UseCallBookingVariantProps {
    trackAssignment?: boolean;
}

const registerCallBookingVariant = (variant: string | null) => {
    if (!variant) return;

    mixpanel.register({
        call_booking_variant: variant,
    });
};

export const useCallBookingVariant = ({
    trackAssignment = false,
}: UseCallBookingVariantProps = {}) => {
    const { applicationEmail } = useContext(ApplicationContext);
    const [callBookingVariant, setCallBookingVariant] = useState<string | null>(
        null,
    );
    const getCallBookingVariationKey = useKameleoonFeature(
        KAMELEOON_FEATURE.call_booking_redesign,
    );
    const { initialize, isInitialized } = useInitialize();

    useEffect(() => {
        if (!applicationEmail) return;

        const loadCallBookingVariant = async () => {
            if (!isInitialized()) {
                await initialize();
            }

            const variant = await getCallBookingVariationKey(applicationEmail);

            setCallBookingVariant(
                variant === ExperimentsConfig.Variants.B
                    ? ExperimentsConfig.Variants.B
                    : ExperimentsConfig.Variants.A,
            );
            registerCallBookingVariant(variant);

            if (trackAssignment) {
                sendFeatureToMixPanel(
                    variant,
                    KAMELEOON_FEATURE.call_booking_redesign,
                );
            }
        };

        loadCallBookingVariant().catch(() => {
            setCallBookingVariant(ExperimentsConfig.Variants.A);
        });
    }, [
        applicationEmail,
        getCallBookingVariationKey,
        initialize,
        isInitialized,
        trackAssignment,
    ]);

    return callBookingVariant;
};
